import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { BaseInputComponent } from '@sharedComponents/form/base-input.component';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { DefaultInputErrorStateMatcher } from '@sharedComponents/form/base-input.error-state-matcher';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { isString } from '@shared/functions/is-string';
import { TolgeeService } from '@core/translation/tolgee.service';

export interface ChipListInputItem {
    [key: string]: any;
    disabled?: boolean;
    disabledReason?: string;
}

@Component({
    selector: 'u2b-chip-list-input',
    templateUrl: './chip-list-input.component.html',
    styleUrls: ['./chip-list-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipListInputComponent),
            multi: true
        },
        {
            provide: MatFormFieldControl,
            useExisting: ChipListInputComponent
        }
    ],
    standalone: false
})
export class ChipListInputComponent extends BaseInputComponent<ChipListInputItem[]> implements OnInit, AfterViewInit {

    itemCtrl = new FormControl();

    errorStateMatcher: DefaultInputErrorStateMatcher;

    controlType = 'chip-list';

    selectedItems: ChipListInputItem[] = [];

    _allItems: ChipListInputItem[];

    get allItems() {
        if (this.disableConditionCallback) {
            this.disableConditionCallback(this.selectedItems, this._allItems);
        }
        return this._allItems;
    }

    filteredItems: Observable<ChipListInputItem[]>;

    currentFilteredItems: ChipListInputItem[] = [];

    @ViewChild('chipListAutocomplete', { static: false }) matAutocomplete?: MatAutocomplete;

    @ViewChild('trigger', {static: true}) autocompleteTrigger!: MatAutocompleteTrigger;

    @ViewChild('input') chipInput!: ElementRef<HTMLInputElement>;


    @Input() hasRemove: boolean;

    @Input() autoFocus: boolean;

    @Input() dataObservable: Observable<ChipListInputItem[]>;

    @Input() items: ChipListInputItem[];

    @Input() set selectedFilterOption(value: 'Volltext' | 'Beginnt mit') {
        this._selectedFilterOption.next(value || 'Volltext');
    }

    _selectedFilterOption = new BehaviorSubject<'Volltext' | 'Beginnt mit'>('Volltext');

    @Input() dataIdentAttribute = 'name';

    @Input() dataLabelAttribute = 'name';

    @Input() disableConditionCallback?: (list: ChipListInputItem[], currentItem: ChipListInputItem) => void;

    ngOnInit(): void {
        super.ngOnInit();

        if (!this.placeholder) {
            this.placeholder = TolgeeService.translate('common.tagsInputPlaceholder');
        }

        if (this.ngControl) {
            this.errorStateMatcher = new DefaultInputErrorStateMatcher(this.ngControl);
        }

        if (this.dataObservable) {
            this.dataObservable.subscribe(data => {
                this.initItems(data);
            });
        } else {
            this.initItems(this.items);
        }
    }

    ngAfterViewInit(): void {
        if (this.autoFocus && this.filteredItems) {
            this.chipInput?.nativeElement?.focus();
            setTimeout(() => {
                if (this.matAutocomplete && this.autocompleteTrigger) {
                    this.autocompleteTrigger?.openPanel();
                }
            }, 0);
        }
    }

    initItems(givenItems: ChipListInputItem[]) {
        this._allItems = givenItems;

        this.filteredItems = combineLatest([
            this._selectedFilterOption,
            this.itemCtrl.valueChanges.pipe(startWith(null))
        ]).pipe(
            map(
                ([selectedFilterOption, item]: ['Volltext' | 'Beginnt mit', string]) =>
                    (isString(item) ? this._filter(item, selectedFilterOption) : this.allItems.slice())
            ),
            tap(items => this.currentFilteredItems = items)
        );
    }

    displayWith(item: ChipListInputItem): string {
        return item?.[this.dataLabelAttribute] || item?.[this.dataIdentAttribute] || '';
    }

    selected(event: any): void {
        const selectedItem = event.option.value;

        if (!selectedItem) {
            return;
        }

        if (!this.selectedItems.includes(selectedItem)) {
            this.selectedItems.push(selectedItem);
        }

        if (!isString(this.itemCtrl.value)) {
            this.clearInput();
        }

        this.value = [...this.selectedItems];
    }

    clearInput(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.itemCtrl.setValue(null);
        this.itemCtrl.reset();
    }

    /** Closes the suggestion panel (sticky header X). mousedown + blur so MDC disabled options still receive the event. (#7171) */
    closeAutocompletePanel(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.autocompleteTrigger?.closePanel();
        this.chipInput?.nativeElement?.blur();
    }

    addChip($event: MatChipInputEvent, input: HTMLInputElement): void {
        if (this.dataObservable || !$event.value) {
            return;
        }
        input.value = null;
        this.selectedItems.push({[this.dataIdentAttribute]: $event.value});
        this.value = [...this.selectedItems];
        this.clearInput();
    }

    addAllFromFilteredList() {
        try {
            for (const item of this.currentFilteredItems) {
                // Re-run the condition on every iteration: adding one item can disable others
                // (e.g. a tenant relation whose payable option no longer matches the selection),
                // and those must not slip in here when picking them one by one is blocked.
                this.disableConditionCallback?.(this.selectedItems, this._allItems);

                if (!item || item.disabled || this.selectedItems.includes(item)) {
                    continue;
                }

                this.selectedItems.push(item);
            }
        } finally {
            // The chips render straight off selectedItems, so the control has to be written even if
            // a consumer's disableConditionCallback threw mid-loop - otherwise the form would hold a
            // different selection than the one on screen. The error itself still propagates.
            this.clearInput();

            this.value = [...this.selectedItems];
        }
    }

    removeChip(chip: ChipListInputItem, trigger: MatAutocompleteTrigger): void {
        this.selectedItems.splice(this.selectedItems.findIndex(currChip => currChip[this.dataIdentAttribute] === chip[this.dataIdentAttribute]), 1);
        this.value = [...this.selectedItems];
        this.itemCtrl.updateValueAndValidity();
        this.clearInput();
        trigger.updatePosition();
    }

    writeValue(obj: any): void {
        super.writeValue(obj);
        this.selectedItems = super.value || [];
    }

    private _filter(value: string, selectedFilterOption: 'Volltext' | 'Beginnt mit'): any[] {
        const filterValue = value.toLowerCase();
        switch (selectedFilterOption) {
            case 'Volltext':
                return this.allItems.filter(item => item[this.dataIdentAttribute].toLowerCase().includes(filterValue));
            case 'Beginnt mit':
                return this.allItems.filter(item => item[this.dataIdentAttribute].toLowerCase().startsWith(filterValue));
        }

    }
}
